import { Spin } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";

import { listMyStudySources, type StudySourceItem } from "@/services/studySource";
import { getTeamsChannelName } from "@/utils/teamsChannelUrl";

import { DropdownOption, FilterDropdown, PermissionEmptyState } from "./DocShared";

function getConnectionMeta(connectionType: StudySourceItem["connectionType"]) {
  return connectionType === 0
    ? { label: "Teams Channel", icon: "ri-microsoft-line" }
    : { label: "SharePoint", icon: "ri-share-line" };
}

function getSourceDisplayName(source: StudySourceItem): string {
  // SharePoint 连接以 Source Site（+ Drive）为锚点，直接展示；Teams 连接从 URL 末段取名。
  if (source.connectionType === 1) {
    if (source.sourceSite) {
      return source.sourceDrive ? `${source.sourceSite}/${source.sourceDrive}` : source.sourceSite;
    }
    return source.studyName || source.studyCode;
  }

  return (
    source.channelNameFromUrl ||
    getTeamsChannelName(source.sourceUrl) ||
    source.studyName ||
    source.studyCode
  );
}

/** 卡片统一展示 SharePoint Site 地址；旧接口未返回完整 URL 时用 SourceSite 兜底。 */
function getSharePointSiteAddress(source: StudySourceItem): string {
  return source.sharePointSiteUrl || source.sourceSite;
}

function StudySourceCard({ source, isZh }: { source: StudySourceItem; isZh: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const connection = getConnectionMeta(source.connectionType);
  const hasPaths = source.includedPaths.length > 0 || source.excludedPaths.length > 0;
  const sharePointSiteAddress = getSharePointSiteAddress(source);

  const copySharePointSiteAddress = () => {
    if (!sharePointSiteAddress) return;
    void navigator.clipboard.writeText(sharePointSiteAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white">
      <div className="w-full flex items-center gap-4 px-5 py-4">
        <div
          className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0"
          style={{ backgroundColor: "rgba(131,0,81,0.08)" }}
        >
          <i className={`${connection.icon} text-lg`} style={{ color: "#830051" }}></i>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-gray-800">{getSourceDisplayName(source)}</span>
            <span
              className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
              style={{ backgroundColor: "rgba(131,0,81,0.1)", color: "#830051" }}
            >
              {connection.label}
            </span>
            <span className="text-[10px] font-semibold font-mono px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
              {source.studyCode}
            </span>
          </div>

          <div className="flex items-center mt-0.5 min-w-0">
            <p className="text-[11px] text-gray-400 font-mono truncate min-w-0">
              {sharePointSiteAddress}
            </p>
            {sharePointSiteAddress ? (
              <button
                type="button"
                onClick={copySharePointSiteAddress}
                title={
                  copied
                    ? isZh
                      ? "已复制"
                      : "Copied!"
                    : isZh
                      ? "复制 SharePoint Site 地址"
                      : "Copy SharePoint Site URL"
                }
                className="shrink-0 ml-2 w-5 h-5 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
                style={{ color: "#830051" }}
              >
                <i className={`${copied ? "ri-check-line" : "ri-file-copy-line"} text-xs`}></i>
              </button>
            ) : null}
            {source.sharePointSiteUrl ? (
              <a
                href={source.sharePointSiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                title={isZh ? "在 SharePoint 中打开" : "Open in SharePoint"}
                className="shrink-0 ml-2 w-5 h-5 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
                style={{ color: "#830051" }}
              >
                <i className="ri-external-link-line text-xs"></i>
              </a>
            ) : null}
          </div>
          {source.remark ? (
            <p className="text-[11px] text-gray-500 mt-1 line-clamp-2">{source.remark}</p>
          ) : null}
        </div>

        {hasPaths ? (
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer shrink-0"
            title={
              isZh
                ? expanded
                  ? "收起路径"
                  : "展开路径"
                : expanded
                  ? "Collapse paths"
                  : "Expand paths"
            }
          >
            <i
              className={`ri-arrow-down-s-line text-sm text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`}
            ></i>
          </button>
        ) : null}
      </div>

      {expanded && hasPaths ? (
        <div className="border-t border-gray-100 bg-gray-50/50 px-5 py-3 space-y-3">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-folder-3-line text-xs text-gray-400"></i>
              <span className="text-xs font-medium text-gray-500">
                {isZh ? "包含路径" : "Included Paths"}
              </span>
              <span className="text-[10px] text-gray-400">({source.includedPaths.length})</span>
            </div>
            {source.includedPaths.length > 0 ? (
              <div className="space-y-1.5">
                {source.includedPaths.map((path) => (
                  <div
                    key={path}
                    className="border border-gray-100 rounded-lg bg-white px-3 py-2 flex items-center gap-2"
                  >
                    <i className="ri-folder-3-line text-xs text-amber-500 shrink-0"></i>
                    <span className="text-xs text-gray-700 font-mono break-all">{path}</span>
                  </div>
                ))}
              </div>
            ) : (
              <span className="text-xs text-gray-300">-</span>
            )}
          </div>

          {source.excludedPaths.length > 0 ? (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-folder-forbid-line text-xs text-gray-400"></i>
                <span className="text-xs font-medium text-gray-500">
                  {isZh ? "排除路径" : "Excluded Paths"}
                </span>
                <span className="text-[10px] text-gray-400">({source.excludedPaths.length})</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {source.excludedPaths.map((path) => (
                  <span
                    key={path}
                    className="inline-flex items-center text-[10px] text-gray-500 font-mono bg-gray-100 rounded px-2 py-1 break-all"
                  >
                    {path}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function StatCard({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-100 bg-white">
      <div
        className="w-9 h-9 flex items-center justify-center rounded-lg shrink-0"
        style={{ backgroundColor: "#83005112" }}
      >
        <i className="ri-folder-chart-line text-sm" style={{ color: "#830051" }}></i>
      </div>
      <div>
        <p className="text-lg font-bold text-gray-800">{value}</p>
        <p className="text-xs text-gray-400">{label}</p>
      </div>
    </div>
  );
}

export default function StudyDocumentsTab({ isZh }: { isZh: boolean }) {
  const [sources, setSources] = useState<StudySourceItem[]>([]);
  const [filterStudy, setFilterStudy] = useState("all");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const loadSources = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const result = await listMyStudySources();
      setSources(result.data);
    } catch {
      setSources([]);
      setLoadError(
        isZh
          ? "Study Source 加载失败，请稍后重试。"
          : "Failed to load Study Sources. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [isZh]);

  useEffect(() => {
    void loadSources();
  }, [loadSources]);

  const availableStudies = useMemo(() => {
    const studyMap = new Map<string, { id: string; code: string; name: string }>();
    sources.forEach((source) => {
      studyMap.set(source.studyId, {
        id: source.studyId,
        code: source.studyCode,
        name: source.studyName,
      });
    });
    return Array.from(studyMap.values());
  }, [sources]);

  const filtered = useMemo(
    () =>
      filterStudy === "all" ? sources : sources.filter((source) => source.studyId === filterStudy),
    [filterStudy, sources],
  );

  const visibleStudyCount = useMemo(
    () => new Set(filtered.map((source) => source.studyId)).size,
    [filtered],
  );

  const studyLabel =
    filterStudy === "all"
      ? "All Studies"
      : (availableStudies.find((study) => study.id === filterStudy)?.name ?? "All Studies");

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center" aria-label="Loading Study Sources">
        <Spin />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col flex-1 items-center justify-center gap-4 px-6">
        <PermissionEmptyState
          icon="ri-error-warning-line"
          title={isZh ? "加载失败" : "Unable to Load Study Sources"}
          desc={loadError}
        />
        <button
          type="button"
          onClick={() => void loadSources()}
          className="px-4 py-1.5 text-xs font-medium text-white rounded-lg cursor-pointer hover:opacity-90"
          style={{ backgroundColor: "#830051" }}
        >
          {isZh ? "重试" : "Retry"}
        </button>
      </div>
    );
  }

  if (sources.length === 0) {
    return (
      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="px-6 pt-4 pb-3">
          <span className="text-xs text-gray-400 whitespace-nowrap">
            0 {isZh ? "个来源" : "sources"}
          </span>
        </div>
        <div className="flex-1 overflow-y-auto px-6 pb-4 flex items-center justify-center">
          <PermissionEmptyState
            icon="ri-shield-keyhole-line"
            title={isZh ? "暂无 Study Source" : "No Study Sources"}
            desc={
              isZh
                ? "当前 Study 分配或 Source 成员关系下暂无可用来源，请联系管理员。"
                : "No Study Sources are available through your study assignments or source memberships. Please contact your administrator."
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="px-6 pt-4 pb-3 flex items-center gap-2 flex-wrap">
        <FilterDropdown label={studyLabel} active={filterStudy !== "all"}>
          <DropdownOption
            label="All Studies"
            active={filterStudy === "all"}
            onClick={() => setFilterStudy("all")}
          />
          {availableStudies.map((study) => (
            <DropdownOption
              key={study.id}
              label={`${study.name} (${study.code})`}
              active={filterStudy === study.id}
              onClick={() => setFilterStudy(study.id)}
              icon="ri-flask-line"
            />
          ))}
        </FilterDropdown>

        <span className="text-xs text-gray-400 whitespace-nowrap">
          {filtered.length}{" "}
          {isZh ? "个来源" : filtered.length === 1 ? "Study Source" : "Study Sources"}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto px-6 pb-4">
        <div className="space-y-3">
          {filtered.map((source) => (
            <StudySourceCard key={source.id} source={source} isZh={isZh} />
          ))}
        </div>
      </div>
    </div>
  );
}
